/** Pure chip state helpers for DocUploadModal — unit-tested. */

export type ChipPhase = 'idle' | 'ready' | 'uploading' | 'success' | 'error'

export type ChipIconKind = 'pdf' | 'image' | 'doc' | 'other'

export function chipIconKind(filename: string, mime?: string | null): ChipIconKind {
  const lower = (filename || '').toLowerCase()
  const m = (mime || '').toLowerCase()
  if (m.includes('pdf') || lower.endsWith('.pdf')) return 'pdf'
  if (m.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/.test(lower)) return 'image'
  if (/\.(docx?|xlsx?|csv)$/.test(lower) || m.includes('word') || m.includes('sheet')) return 'doc'
  return 'other'
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function humanUploadError(raw: string): string {
  const s = String(raw || '').toLowerCase()
  if (s.includes('file too large') || s.includes('limit') || s.includes('25')) {
    return 'File too large (max 25 MB)'
  }
  if (s.includes('invalid file type') || s.includes('mime') || s.includes('accepted types')) {
    return 'Wrong file type — use PDF, DOCX, XLSX, JPG, or PNG'
  }
  if (s.includes('network') || s.includes('failed to fetch') || s.includes('load failed')) {
    return 'Network error — check your connection and try again'
  }
  return raw || 'Upload failed'
}

export function nextChipPhase(
  current: ChipPhase,
  event: 'pick' | 'start' | 'done' | 'fail' | 'clear',
): ChipPhase {
  if (event === 'clear') return 'idle'
  if (event === 'pick') return 'ready'
  if (event === 'start') return 'uploading'
  if (event === 'done') return 'success'
  if (event === 'fail') return 'error'
  return current
}

/** Client-side duplicate: same name + size among non-deleted rows. */
export function isDuplicateFile(
  file: { name: string; size: number },
  existing: Array<{ original_name?: string; file_size?: number }>,
): boolean {
  const n = file.name.toLowerCase()
  return existing.some(
    (e) =>
      String(e.original_name || '').toLowerCase() === n &&
      Number(e.file_size) === Number(file.size),
  )
}

export function duplicateWarnMessage(filename: string): string {
  return `Looks like ${filename} is already here — upload anyway?`
}
